// src/pages/HomePage.tsx
import {
  Box,
  Typography,
  Button,
  Stack,
  Paper,
  Snackbar,
  Dialog,
  DialogContent,
  useTheme,
} from '@mui/material'

import CardLogo from '../assets/vip/card-logo.png'
import Grid from '@mui/material/Grid'
import Layout from '../Layout'
import BgImg1 from '../assets/vip/background-1.jpg'

import BgImg2 from '../assets/vip/background-2.jpg'
import coinImg from '../assets/vip/token.png'
import VIPTable, { allTiers } from '../components/VIPTable'
import defaultCard from '../assets/vip/cooper.png'
import BannerSection from '../components/BannerSection'
import { getProfile, postMonthlyClaim, postTierClaim, UserProfile } from '../api'
import { QueryClient, useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import TierStatus from '../components/TierStatus'
import jetpotVipGif from '../assets/vip/Jetpot-vip-icon.gif'
import { subtitleShadow } from '../utils/styles'

export const tiers = [
  { key: 'copper', icon: require('../assets/vip/cooper.png') },
  { key: 'bronze', icon: require('../assets/vip/bronze.png') },
  { key: 'silver', icon: require('../assets/vip/silver.png') },
  { key: 'gold', icon: require('../assets/vip/gold.png') },
  { key: 'crystal', icon: require('../assets/vip/crystal.png') },
  { key: 'platinum', icon: require('../assets/vip/platinum.png') },
  { key: 'diamond', icon: require('../assets/vip/diamond.png') },
  { key: 'elite', icon: require('../assets/vip/elite.png') },
  { key: 'master', icon: require('../assets/vip/master.png') },
]

const HomePage = () => {
  const [searchParams] = useSearchParams()
  const userId = searchParams.get('user_id')?.toLowerCase()?.replace(/a/g, '4')
  const id = userId
  useEffect(() => {
    if (!userId) {
      window.location.href = 'https://jetpot.vip/login'
    }
  }, [userId])

  const {
    data: profile,
    isLoading: isLoadingProfile,
    error: profileError,
  } = useQuery<UserProfile>({
    queryKey: ['getProfile', userId],
    queryFn: () => getProfile(userId!),
    enabled: !!userId,
    retry: false,
  })

  const now = profile?.tier.name || ''
  const [showToast, setShowToast] = useState(false)
  const [snackbarMsg, setSnackbarMsg] = useState('')
  const [showCongratulationsPopup, setShowCongratulationsPopup] = useState(false)
  const [popupType, setPopupType] = useState<'monthly' | 'tier'>('monthly')
  const queryClient = new QueryClient()
  const INTERACTION_EVENTS = ['pointerdown', 'wheel', 'scroll']
  const theme = useTheme()

  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const waitingForGesture = useRef(false)

  useEffect(() => {
    if (!showToast) return

    waitingForGesture.current = true

    const onFirstInteract = () => {
      if (!waitingForGesture.current) return
      waitingForGesture.current = false
      toastTimer.current = setTimeout(() => setShowToast(false), 5000)
      INTERACTION_EVENTS.forEach((e) => window.removeEventListener(e, onFirstInteract))
    }

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        if (toastTimer.current) clearTimeout(toastTimer.current)
        toastTimer.current = null
        waitingForGesture.current = true
        INTERACTION_EVENTS.forEach((e) => window.removeEventListener(e, onFirstInteract))
      } else if (waitingForGesture.current) {
        INTERACTION_EVENTS.forEach((e) =>
          window.addEventListener(e, onFirstInteract, { once: true }),
        )
      }
    }

    INTERACTION_EVENTS.forEach((e) => window.addEventListener(e, onFirstInteract, { once: true }))
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      INTERACTION_EVENTS.forEach((e) => window.removeEventListener(e, onFirstInteract))
      document.removeEventListener('visibilitychange', onVisibility)
      if (toastTimer.current) clearTimeout(toastTimer.current)
      toastTimer.current = null
      waitingForGesture.current = false
    }
  }, [showToast])

  const handleClaimMonthly = () => {
    const id = profile?.unclaimed_monthly_bonus?.transaction_id
    if (id && profile?.user_id) {
      monthlyClaimMutation.mutate({
        transaction_id: id,
        user_id: profile.user_id,
      })
    }
  }
  const monthlyClaimMutation = useMutation({
    mutationFn: postMonthlyClaim,
    onSuccess: (res) => {
      setPopupType('monthly')
      setShowCongratulationsPopup(true)
      queryClient.invalidateQueries({ queryKey: ['getProfile', id] })
      setTimeout(() => {
        window.open('https://jetpot.vip/promotion', '_blank')
      }, 2000)
    },
    onError: (err: any) => {
      setSnackbarMsg(err?.response?.data?.message || 'Claim failed.')
      setShowToast(true)
    },
  })

  const handleClaimTier = () => {
    const id = profile?.unclaimed_tier_bonus?.transaction_id
    if (id && profile?.user_id) {
      tierClaimMutation.mutate({
        transaction_id: id,
        user_id: profile.user_id,
      })
    }
  }
  const tierClaimMutation = useMutation({
    mutationFn: postTierClaim,
    onSuccess: (res) => {
      setPopupType('tier')
      setShowCongratulationsPopup(true)
      queryClient.invalidateQueries({ queryKey: ['getProfile', id] })
      setTimeout(() => {
        window.open('https://jetpot.vip/promotion', '_blank')
      }, 2000)
    },
    onError: (err: any) => {
      setSnackbarMsg(err?.response?.data?.message || 'Claim failed.')
      setShowToast(true)
    },
  })

  return (
    <Layout>
      <BannerSection />
      <Box
        pb={6}
        sx={{
          // position: "relative",
          // pt: { xs: 8, md: 14 },
          // pb: { xs: 12, md: 18 },
          // px: { xs: 2, md: 8 },
          backgroundImage: {
            xs: `url(${BgImg1})`,
            // md: `url(${aboutBg})`,
          },
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          color: 'white',
          // zIndex: 2,
          // overflow: "hidden",
        }}
      >
        <Box
          sx={{
            maxWidth: 'lg',
            mx: 'auto',
            px: 2,
          }}
          py={6}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 2,
              alignItems: 'center',
            }}
          >
            {/* CardLogo for md+ (original) */}
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                px: 2,
              }}
            >
              <Box
                component="img"
                src={CardLogo}
                sx={{
                  width: '100%',
                  maxWidth: { xs: '65%', md: '500px' },
                  flexGrow: 1,
                  objectFit: 'contain',
                  height: 'auto',
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: '20px',
                  border: `1px solid ${theme.palette.light.main}`,

                  background:
                    'linear-gradient(to right, rgba(255,255,255,0.2), rgba(153,153,153,0.2))',
                  backdropFilter: 'blur(4px)',

                  display: { xs: 'flex', md: 'none' },

                  p: { xs: 1, md: 3 },
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight="900"
                  sx={{
                    color: 'light.main',
                    display: { xs: 'flex', md: 'none' },
                    textTransform: 'uppercase',
                    fontSize: '0.55rem',
                    textShadow: `
                1px 1px 0 #630202,
               -1px -1px 0 #630202,
                1px -1px 0 #630202,
               -1px 1px 0 #630202,0px 2px 2px #6a6a6a
              `,
                  }}
                >
                  current status
                </Typography>
              </Paper>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: '20px',
                  border: `1px solid ${theme.palette.light.main}`,

                  background:
                    'linear-gradient(to right, rgba(255,255,255,0.2), rgba(153,153,153,0.2))',
                  backdropFilter: 'blur(4px)',
                  flexWrap: 'wrap', // allows Deposit Progress to wrap
                  alignItems: 'start',
                  justifyContent: 'space-between',
                  gap: 2,
                  display: { xs: 'flex', md: 'none' },

                  p: { xs: 1, md: 3 },
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight="900"
                  sx={{
                    color: 'primary.dark',
                    textTransform: 'uppercase',
                    fontSize: { xs: '0.55rem', md: '1.25rem' },
                    minWidth: 'fit-content',
                    flexShrink: 0,
                    ...subtitleShadow,
                  }}
                >
                  Deposit Progress:
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 0.5,
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    minWidth: 'fit-content',
                  }}
                >
                  <Typography
                    variant="h6"
                    color="#f59696"
                    sx={{
                      fontSize: { xs: '0.55rem', md: '1.15rem' },
                      fontWeight: 900,
                      textShadow: '0px 3px 2px #6a6a6a',
                    }}
                  >
                    {profile?.lifetime_deposit?.toLocaleString() ?? '0'} /
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: { xs: '0.55rem', md: '1.15rem' },
                      color: 'secondary.main',
                      fontWeight: 900,
                      textShadow: '0px 3px 2px #6a6a6a',
                    }}
                  >
                    {typeof profile?.tier?.max_points === 'number'
                      ? (profile.tier.max_points + 1).toLocaleString()
                      : '—'}
                  </Typography>
                </Box>
              </Paper>
            </Box>
            {/* CardLogo + TierStatus for xs/sm */}
            <Box
              sx={{
                display: { xs: 'flex', md: 'none' },
                flexDirection: 'row',
                alignItems: 'center',
                width: '100%',
                gap: '0.25rem',
                overflowX: 'auto',
              }}
            >
              <Box
                component="img"
                src={CardLogo}
                sx={{
                  width: '90px',
                  minWidth: '90px',
                  maxWidth: '120px',
                  objectFit: 'contain',
                  height: 'auto',
                  mr: 1,
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <TierStatus now={now} />
              </Box>
            </Box>
            <Box
              sx={{
                display: { xs: 'none', md: 'block' },
                flex: 1,
                minWidth: 0,
              }}
            >
              <TierStatus now={now} />
            </Box>
          </Box>
          <Grid container spacing={2} sx={{ mt: { xs: '0', md: 2 } }} justifyContent="center">
            <Grid size={{ xs: 12, md: 5 }}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: '20px',
                  display: { xs: 'none', md: 'flex' },
                  border: `2px solid ${theme.palette.secondary.main}`,
                  background: 'transparent',
                  flexWrap: 'wrap', // allows Deposit Progress to wrap
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 0.25,
                  p: { xs: 1, md: 3 },
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight="900"
                  sx={{
                    color: 'primary.dark',
                    textTransform: 'uppercase',
                    fontSize: { xs: '0.75rem', md: '1.25rem' },
                    minWidth: 'fit-content',
                    flexShrink: 0,
                    ...subtitleShadow,
                  }}
                >
                  Deposit Progress:
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 1,
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    minWidth: 'fit-content',
                  }}
                >
                  <Typography
                    variant="h6"
                    color="#f59696"
                    sx={{
                      fontSize: { xs: '0.75rem', md: '1.15rem' },
                      fontWeight: 900,
                      textShadow: '0px 3px 2px #6a6a6a',
                    }}
                  >
                    {profile?.lifetime_deposit?.toLocaleString() ?? '0'} /
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: { xs: '0.75rem', md: '1.15rem' },
                      color: 'secondary.main',
                      fontWeight: 900,
                      textShadow: '0px 3px 2px #6a6a6a',
                    }}
                  >
                    {typeof profile?.tier?.max_points === 'number'
                      ? (profile.tier.max_points + 1).toLocaleString()
                      : '—'}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}></Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mt: { xs: -2, md: 0 },
                }}
              >
                <Box
                  component="img"
                  src={
                    allTiers.find((t) => t.key.toLowerCase() === profile?.tier?.name?.toLowerCase())
                      ?.icon ?? defaultCard
                  }
                  alt="vip-card"
                  sx={{ width: { xs: '60%', md: '100%' } }}
                />
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 7 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  flexDirection: 'column',
                  gap: 1,
                  height: '100%',
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 1, md: 3 },
                    borderRadius: '20px',
                    border: `2px solid ${theme.palette.secondary.main}`,
                    background: 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                  }}
                >
                  <Box sx={{ alignItems: 'end' }}>
                    <Typography
                      variant="h6"
                      fontWeight="700"
                      sx={{
                        color: 'primary.dark',
                        textTransform: 'uppercase',
                        fontSize: { xs: '0.75rem', md: '1.4rem' },
                        ...subtitleShadow,
                      }}
                    >
                      Monthly Bonus
                    </Typography>

                    <Typography
                      variant="h4"
                      fontWeight="bold"
                      sx={{
                        color: 'secondary.main',
                        fontWeight: 900,
                        textShadow: '0px 1px 2px #9d9999',
                        // fontSize: { xs: "0.75rem", md: "1.4rem" },
                      }}
                    >
                      {profile?.unclaimed_monthly_bonus?.amount?.toLocaleString() ?? '—'}
                    </Typography>
                  </Box>

                  <Button
                    variant="contained"
                    size="small"
                    sx={{
                      borderRadius: '12px',
                      px: 4,
                      py: { xs: 1, md: 1.5 },
                      bgcolor: 'primary.main',
                      color: 'light.main',
                      fontWeight: 'bold',
                      fontSize: { xs: '0.75rem', md: '1rem' },
                      border: `2px solid ${theme.palette.secondary.main}`,
                      '&:hover': {
                        bgcolor: 'primary.main',
                      },
                    }}
                    onClick={handleClaimMonthly}
                  >
                    CLAIM
                  </Button>
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: '20px',
                    border: `2px solid ${theme.palette.secondary.main}`,
                    background: 'transparent',
                    p: 0,
                    display: 'flex',
                    alignItems: 'end',
                    justifyContent: 'space-between',
                    flexDirection: 'row',
                    gap: 2,
                  }}
                >
                  <Box sx={{ p: { xs: 1, md: 4 } }}>
                    <Typography
                      variant="h6"
                      fontWeight="700"
                      sx={{
                        color: 'primary.main',
                        display: { xs: 'flex', md: 'none' },
                        textTransform: 'uppercase',
                        fontSize: { xs: '0.75rem', md: '1.4rem' },
                        ...subtitleShadow,
                      }}
                    >
                      Tier Upgrade Bonus
                    </Typography>
                    <Stack spacing={0}>
                      <Typography
                        variant="h6"
                        fontWeight="700"
                        sx={{
                          color: 'primary.main',
                          display: { xs: 'none', md: 'flex' },
                          textTransform: 'uppercase',
                          fontSize: { xs: '0.75rem', md: '1.4rem' },
                          ...subtitleShadow,
                        }}
                      >
                        Tier Upgrade Bonus
                      </Typography>

                      <Typography
                        variant="caption"
                        sx={{
                          color: 'primary.main',
                          textTransform: 'uppercase',
                          fontWeight: 700,
                          fontSize: { xs: 10, md: 12 },
                        }}
                      >
                        *Auto credit to your account
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          color: 'secondary.main',
                          mt: 1,
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          fontWeight: '400',
                        }}
                        onClick={handleClaimTier}
                      >
                        CHECK HERE
                      </Typography>
                    </Stack>
                  </Box>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Typography
                      variant="h4"
                      fontWeight="bold"
                      sx={{
                        color: 'secondary.main',
                        ...subtitleShadow,
                        // fontSize: { xs: "0.75rem", md: "1.4rem" },
                      }}
                    >
                      {profile?.unclaimed_tier_bonus?.amount?.toLocaleString() ?? '—'}
                    </Typography>

                    <Box
                      component="img"
                      src={coinImg}
                      alt="coin"
                      sx={{
                        width: 'auto',
                        height: '100%',
                        maxHeight: { xs: '80px', md: '120px' },
                        pr: 2,
                      }}
                    />
                  </Stack>
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>

      <Box
        px={{ xs: 1, md: 8 }}
        py={6}
        sx={{
          backgroundColor: 'primary.dark',
          backgroundImage: `url(${BgImg2})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundBlendMode: 'overlay',
        }}
      >
        <Box
          sx={{
            maxWidth: 'lg',
            mx: 'auto',
            px: 2,
          }}
          py={6}
        >
          <VIPTable />
          {/* <Button
            onClick={() => {
              setSnackbarMsg("Monthly bonus claimed!");
              setShowToast(true);
              setTimeout(() => {
                window.open("https://jetpot.vip/", "_blank");
              }, 500); // let snackbar show before redirect
            }}
          >
            oi
          </Button> */}
        </Box>
      </Box>
      <Snackbar
        open={showToast}
        message={snackbarMsg}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        sx={{
          top: '80dvh',
        }}
      />

      <Dialog
        open={showCongratulationsPopup}
        onClose={() => setShowCongratulationsPopup(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            background: 'linear-gradient(to bottom, #1a1a2e, #16213e)',
            border: '2px solid #D30000',
          },
        }}
      >
        <DialogContent
          sx={{
            textAlign: 'center',
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
          }}
        >
          <Box
            component="img"
            src={jetpotVipGif}
            alt="Congratulations"
            sx={{
              width: '150px',
              height: '150px',
              objectFit: 'contain',
            }}
          />
          <Typography
            variant="h5"
            sx={{
              color: 'light.main',
              fontWeight: 'bold',
              textAlign: 'center',
              lineHeight: 1.4,
            }}
          >
            {popupType === 'monthly'
              ? 'Congratulations! Your monthly bonus has been successfully claimed and credited to your account.'
              : 'Congratulations on achieving your new VIP tier! Your reward is ready on the promotions page.'}
          </Typography>
          <Button
            variant="contained"
            onClick={() => setShowCongratulationsPopup(false)}
            sx={{
              borderRadius: '12px',
              px: 4,
              py: 1.5,
              background: 'linear-gradient(to right, #D30000, #6A002C)',
              color: 'light.main',
              fontWeight: 'bold',
              fontSize: '1rem',
              border: `2px solid ${theme.palette.light.main}`,

              '&:hover': {
                background: 'linear-gradient(to right, #E00000, #80003A)',
              },
            }}
          >
            OK
          </Button>
        </DialogContent>
      </Dialog>
    </Layout>
  )
}

export default HomePage
